/**
 * Luxe · Three.js Studio Viewer (Phase 2B-1).
 *
 * React wrapper around @react-three/fiber + drei OrbitControls. Consumes
 * a canonical DesignScene + project + optional pinned Design Option
 * and mounts a deterministic Scene3DBuilder output. Preserves iPad
 * touch (react-three-fiber handles pointer events automatically).
 */
import { useEffect, useRef, useMemo } from "react";
import * as THREE from "three";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { buildLuxeScene } from "./Scene3DBuilder";
import { mmToM } from "./coordinateAdapter";

// ── Camera presets ─────────────────────────────────────────────
// Derived from scene bounds so a 3000mm cabinet is framed correctly
// on any wall. Presets record eye/target and the camera FOV.
export const CAMERA_PRESETS = {
  front:            { eye_offset: [0.5, 0.5, 3.0], target_offset: [0.5, 0.4, 0.0], fov: 35 },
  perspective:      { eye_offset: [1.2, 0.6, 2.8], target_offset: [0.5, 0.35, 0.0], fov: 32 },
  left_perspective: { eye_offset: [-0.4, 0.6, 2.5], target_offset: [0.5, 0.35, 0.0], fov: 30 },
  right_perspective:{ eye_offset: [1.4, 0.6, 2.5], target_offset: [0.5, 0.35, 0.0], fov: 30 },
  detail:           { eye_offset: [0.5, 0.35, 1.6], target_offset: [0.5, 0.35, 0.0], fov: 28 },
};

/** Compute an eye + target from scene bounds + a preset. */
export function cameraFor(preset, sceneBounds) {
  const p = CAMERA_PRESETS[preset] || CAMERA_PRESETS.perspective;
  const w = sceneBounds.width, h = sceneBounds.height;
  const eye = [
    sceneBounds.x + w * p.eye_offset[0],
    Math.max(0.8, h * p.eye_offset[1]),
    p.eye_offset[2] * Math.max(w, 2.5),
  ];
  const target = [
    sceneBounds.x + w * p.target_offset[0],
    h * p.target_offset[1],
    p.target_offset[2],
  ];
  return { eye, target, fov: p.fov };
}

function SceneMount({ scene, project, sceneVersion, pinsByMaterialId, onReady, preset, camera: camCfg }) {
  const { scene: threeScene, gl, camera } = useThree();
  const addedRef = useRef([]);
  // Sync camera when preset (eye/target/fov) changes. The <Canvas> camera
  // prop only seeds the initial camera; preset changes must imperatively
  // update the live camera or the studio would see a stale angle.
  useEffect(() => {
    if (!camera || !camCfg) return;
    camera.position.set(camCfg.eye[0], camCfg.eye[1], camCfg.eye[2]);
    if (camera.isPerspectiveCamera && camCfg.fov) {
      camera.fov = camCfg.fov;
      camera.updateProjectionMatrix();
    }
    camera.lookAt(camCfg.target[0], camCfg.target[1], camCfg.target[2]);
  }, [preset, camera, camCfg?.eye?.[0], camCfg?.eye?.[1], camCfg?.eye?.[2], camCfg?.fov, camCfg?.target?.[0], camCfg?.target?.[1], camCfg?.target?.[2]]);
  // Expose renderer + scene + camera to the parent panel so the
  // RenderPassManager can drive Phase 2B-2.5 pass generation.
  useEffect(() => {
    onReady?.({ renderer: gl, scene: threeScene, camera });
  }, [gl, threeScene, camera, onReady]);
  useEffect(() => {
    // Clean previous mount's children so re-renders don't stack scenes.
    addedRef.current.forEach((obj) => {
      threeScene.remove(obj);
      obj.traverse?.((o) => {
        if (o.isMesh) {
          o.geometry?.dispose();
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
          else o.material?.dispose();
        }
      });
    });
    addedRef.current = [];
    let built;
    try {
      built = buildLuxeScene(scene, project, sceneVersion, pinsByMaterialId);
    } catch (e) {
      // Defensive — malformed scene should never crash the viewer.
      // Surface once in console; do not throw to R3F which would show
      // a React error overlay.
      // eslint-disable-next-line no-console
      console.warn("Luxe 3D build failed:", e);
      return;
    }
    threeScene.background = built.background;
    const children = [...built.children];
    children.forEach((c) => {
      built.remove(c);
      threeScene.add(c);
      addedRef.current.push(c);
    });
    return () => {
      addedRef.current.forEach((obj) => {
        threeScene.remove(obj);
        obj.traverse?.((o) => {
          if (o.isMesh) {
            o.geometry?.dispose();
            if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
            else o.material?.dispose();
          }
        });
      });
      addedRef.current = [];
    };
  }, [scene, project, sceneVersion, pinsByMaterialId, threeScene]);
  return null;
}

/**
 * Imperative OrbitControls — bypasses R3F v9's <primitive> applyProps
 * (which crashes on drei's OrbitControls with "reading 'file' of undefined").
 * Uses three-stdlib OrbitControls directly, connected to the R3F camera +
 * gl.domElement. This is the sanctioned "raw Three.js OrbitControls
 * controlled wrapper" per the Phase 2B brief.
 */
function LuxeOrbitControls({ target }) {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);
  const controlsRef = useRef(null);
  useEffect(() => {
    if (!camera || !gl?.domElement) return;
    const controls = new ThreeOrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.minDistance = 0.5;
    controls.maxDistance = 20;
    if (target) controls.target.set(target[0], target[1], target[2]);
    controls.update();
    const onChange = () => invalidate();
    controls.addEventListener("change", onChange);
    controlsRef.current = controls;
    return () => {
      controls.removeEventListener("change", onChange);
      controls.dispose();
      controlsRef.current = null;
    };
  }, [camera, gl, invalidate]);
  // Keep target in sync when preset changes.
  useEffect(() => {
    const c = controlsRef.current;
    if (c && target) {
      c.target.set(target[0], target[1], target[2]);
      c.update();
    }
  }, [target?.[0], target?.[1], target?.[2]]);
  useFrame(() => {
    controlsRef.current?.update();
  }, -1);
  return null;
}

/**
 * Public 3D viewer component.
 * Props:
 *   scene           - schematic snapshot (items, walls, etc.)
 *   project         - project doc
 *   sceneVersion    - optional pinned Design Option
 *   pinsByMaterialId- optional {mid: {asset_id, asset_version, ...}}
 *   preset          - camera preset key
 */
export default function Luxe3DViewer({ scene, project, sceneVersion,
                                       pinsByMaterialId = {},
                                       preset = "perspective",
                                       onReady = null }) {
  const bounds = useMemo(() => {
    const wMm = Number(project?.wall_width_mm)  || Number(scene?.wall_width_mm)  || 4000;
    const hMm = Number(project?.wall_height_mm) || Number(scene?.wall_height_mm) || 2700;
    return { x: 0, y: 0, width: mmToM(wMm), height: mmToM(hMm) };
  }, [project?.wall_width_mm, project?.wall_height_mm, scene?.wall_width_mm, scene?.wall_height_mm]);
  const camera = useMemo(() => cameraFor(preset, bounds), [preset, bounds]);
  return (
    <div className="w-full h-full min-h-[520px] bg-neutral-100"
         data-testid="luxe-3d-viewer">
      <Canvas dpr={[1, 2]}
              camera={{ position: camera.eye, fov: camera.fov, near: 0.05, far: 100 }}
              gl={{ preserveDrawingBuffer: true, antialias: true }}
              data-testid="luxe-3d-canvas">
        <SceneMount scene={scene}
                    project={project}
                    sceneVersion={sceneVersion}
                    pinsByMaterialId={pinsByMaterialId}
                    onReady={onReady}
                    preset={preset}
                    camera={camera}/>
        <LuxeOrbitControls target={camera.target}/>
      </Canvas>
    </div>
  );
}
